import {
  EligibleRevenueCandidate,
  EligibleRevenueCandidateStatus,
  EligibleRevenueReviewData,
  EligibleRevenueReviewSummary,
} from "../../types/revenueOps";

export type EligibleRevenueRow = Record<string, unknown>;

export type EligibleRevenueDerivationInput = {
  invoice: EligibleRevenueRow;
  payments?: EligibleRevenueRow[];
  adjustments?: EligibleRevenueRow[];
  account?: EligibleRevenueRow;
  customer?: EligibleRevenueRow;
  attributions?: EligibleRevenueRow[];
  assignments?: EligibleRevenueRow[];
  partners?: EligibleRevenueRow[];
  lines?: EligibleRevenueRow[];
  products?: EligibleRevenueRow[];
  prices?: EligibleRevenueRow[];
  stagedEvents?: EligibleRevenueRow[];
};

function value(row: EligibleRevenueRow | undefined, key: string): string {
  const result = row?.[key];
  return result === undefined || result === null ? "" : String(result);
}

function amount(row: EligibleRevenueRow | undefined, key: string): number {
  const result = Number(row?.[key] || 0);
  return Number.isFinite(result) ? result : 0;
}

function dateOnly(input?: string): string {
  return input ? input.slice(0, 10) : "";
}

function distinctRefundAmount(rows: EligibleRevenueRow[]): number {
  const seen = new Set<string>();
  return rows.filter((row) => value(row, "adjustment_type") === "refund").reduce((sum, row) => {
    const metadata = (row.metadata as EligibleRevenueRow) || {};
    const key = value(metadata, "canonical_refund_key") || value(metadata, "stripe_refund_id") || value(row, "stripe_object_id") || value(row, "id");
    if (seen.has(key)) return sum;
    seen.add(key);
    return sum + Math.abs(amount(row, "amount"));
  }, 0);
}

export function calculateRetainedRevenue(invoice: EligibleRevenueRow): number {
  const paid = Math.max(0, amount(invoice, "amount_paid"));
  const refunded = Math.min(paid, Math.max(0, amount(invoice, "amount_refunded")));
  return Math.max(0, Number((paid - refunded).toFixed(2)));
}

export function buildEligibleRevenueReviewKey(invoiceId: string, paymentId?: string): string {
  return `eligible_revenue:${invoiceId}:${paymentId || "no_payment"}`;
}

function isActiveAttribution(row: EligibleRevenueRow, reviewDate: string): boolean {
  const status = value(row, "status");
  const starts = dateOnly(value(row, "effective_start") || value(row, "commission_start_date"));
  const ends = dateOnly(value(row, "effective_end"));
  return ["active", "approved"].includes(status) && (!starts || starts <= reviewDate) && (!ends || ends >= reviewDate);
}

function isActiveAssignment(row: EligibleRevenueRow, reviewDate: string): boolean {
  const starts = dateOnly(value(row, "starts_at"));
  const ends = dateOnly(value(row, "ends_at"));
  return (!starts || starts <= reviewDate) && (!ends || ends >= reviewDate);
}

function classify(status: EligibleRevenueCandidateStatus, reasons: string[], blockerCodes: string[], input: { invoice: EligibleRevenueRow; payment?: EligibleRevenueRow; account?: EligibleRevenueRow; customer?: EligibleRevenueRow; attributions?: EligibleRevenueRow[]; assignments?: EligibleRevenueRow[]; lines?: EligibleRevenueRow[]; products?: EligibleRevenueRow[]; stagedEvents?: EligibleRevenueRow[] }): EligibleRevenueCandidateStatus {
  const reviewKey = buildEligibleRevenueReviewKey(value(input.invoice, "id"), value(input.payment, "id") || undefined);
  if (input.stagedEvents?.some((event) => value(event, "source_key") === reviewKey || value(event, "invoice_id") === value(input.invoice, "id") || value(event, "payment_id") === value(input.payment, "id"))) return "already_staged";
  return status;
}

export function deriveEligibleRevenueCandidate(input: EligibleRevenueDerivationInput): EligibleRevenueCandidate {
  const invoice = input.invoice;
  const invoiceId = value(invoice, "id");
  const invoiceDate = dateOnly(value(invoice, "paid_at") || value(invoice, "issued_at") || value(invoice, "created_at"));
  const payments = input.payments || [];
  const payment = payments.find((row) => ["succeeded", "refunded", "partially_refunded"].includes(value(row, "status"))) || payments[0];
  const paid = Math.max(0, amount(invoice, "amount_paid"));
  const refunded = Math.min(paid, Math.max(amount(invoice, "amount_refunded"), distinctRefundAmount(input.adjustments || [])));
  const retained = Math.max(0, Number((paid - refunded).toFixed(2)));
  const reasons: string[] = [];
  const blockerCodes: string[] = [];
  let status: EligibleRevenueCandidateStatus = "ready";

  if (value(invoice, "status") !== "paid" || paid <= 0) {
    status = "excluded";
    reasons.push("Invoice is not a paid, collected invoice.");
  } else if (!payment) {
    status = "blocked";
    blockerCodes.push("missing_payment");
    reasons.push("Paid invoice is missing a successful or refunded payment mirror row.");
  } else if (!["succeeded", "refunded", "partially_refunded"].includes(value(payment, "status")) && value(payment, "status") !== "failed") {
    status = "blocked";
    blockerCodes.push("payment_not_collected");
    reasons.push("Payment mirror exists but is not in a collected state.");
  } else if (!input.account) {
    status = "blocked";
    blockerCodes.push("missing_account");
    reasons.push("Account mapping needs review. Select or create the account in Toren's core account system before proceeding.");
  }

  if (input.customer && value(input.customer, "customer_kind") === "agency_wholesale") {
    status = "excluded";
    reasons.push("Agency wholesale customer is not a sales-partner commission relationship.");
  }
  if (retained <= 0 && value(invoice, "status") === "paid") {
    status = "excluded";
    reasons.push("Invoice is fully refunded or has no retained amount.");
  }
  if (payment && value(payment, "status") === "failed") {
    status = "excluded";
    reasons.push("Failed payments are not commission-eligible.");
  }

  const attribution = (input.attributions || []).find((row) => isActiveAttribution(row, invoiceDate));
  if (status === "ready" && !attribution) {
    status = "blocked";
    blockerCodes.push("missing_attribution");
    reasons.push("No active partner attribution was found for the invoice date.");
  }
  const assignment = attribution && (input.assignments || []).find((row) => value(row, "partner_id") === value(attribution, "partner_id") && isActiveAssignment(row, invoiceDate));
  if (status === "ready" && attribution && !assignment) {
    status = "blocked";
    blockerCodes.push("missing_partner_plan");
    reasons.push("Attributed partner has no active commission plan assignment for the invoice date.");
  }

  const lines = input.lines || [];
  if (status === "ready" && lines.length > 0) {
    const products = new Map((input.products || []).map((product) => [value(product, "id"), product]));
    const prices = new Map((input.prices || []).map((price) => [value(price, "id"), price]));
    const mappedProduct = (line: EligibleRevenueRow) => products.get(value(line, "product_id")) || products.get(value(prices.get(value(line, "price_id")), "product_id"));
    const nonCommissionable = lines.find((line) => line.commissionable === false || value(mappedProduct(line), "product_type") === "third_party" || value(mappedProduct(line), "product_type") === "pass_through");
    const unknownMapping = lines.find((line) => !mappedProduct(line));
    if (nonCommissionable) {
      status = "excluded";
      reasons.push("Invoice includes a non-commissionable or non-software line item.");
    } else if (unknownMapping) {
      status = "blocked";
      blockerCodes.push("unknown_product_mapping");
      reasons.push("A line item could not be mapped to a known product or price.");
    }
  } else if (status === "ready" && lines.length === 0) {
    reasons.push("Invoice-level review; line item mapping is unavailable.");
  }
  if (refunded > 0 && status !== "excluded") reasons.push("Refund adjustment reduces retained revenue.");

  const partnerId = value(attribution, "partner_id");
  const partner = (input.partners || []).find((row) => value(row, "id") === partnerId);
  const paymentId = value(payment, "id") || undefined;
  const stagedEvent = (input.stagedEvents || []).find((event) => value(event, "source_key") === buildEligibleRevenueReviewKey(invoiceId, paymentId) || value(event, "invoice_id") === invoiceId || value(event, "payment_id") === paymentId);
  const stagedStatus = classify(status, reasons, blockerCodes, { ...input, payment, stagedEvents: input.stagedEvents });
  return {
    id: invoiceId,
    invoice_id: invoiceId,
    payment_id: paymentId,
    account_id: value(invoice, "account_id") || undefined,
    invoice_number: value(invoice, "invoice_number") || undefined,
    stripe_invoice_id: value(invoice, "stripe_invoice_id") || undefined,
    account_name: input.account ? (value(input.account, "display_name") || value(input.account, "business_name") || value(input.account, "account_name") || value(input.account, "website_domain") || value(invoice, "account_id") || undefined) : "Account mapping needs review",
    account_mapping_issue: !input.account && Boolean(value(invoice, "account_id")),
    invoice_date: value(invoice, "issued_at") || value(invoice, "created_at") || undefined,
    paid_at: value(invoice, "paid_at") || undefined,
    amount_paid: paid,
    amount_refunded: refunded,
    retained_amount: retained,
    currency: value(invoice, "currency") || "usd",
    payment_status: value(payment, "status") || undefined,
    status: stagedStatus,
    reasons,
    blocker_codes: blockerCodes,
    attribution_id: value(attribution, "id") || undefined,
    partner_id: partnerId || undefined,
    plan_id: value(assignment, "plan_id") || undefined,
    partner_name: value(partner, "display_name") || value(partner, "legal_name") || undefined,
    plan_name: value(assignment, "plan_name") || undefined,
    staged_event_id: value(stagedEvent, "id") || undefined,
    refund_adjusted: refunded > 0,
    missing_attribution: !attribution,
    review_key: buildEligibleRevenueReviewKey(invoiceId, paymentId),
  };
}

export function summarizeEligibleRevenueCandidates(candidates: EligibleRevenueCandidate[]): EligibleRevenueReviewSummary {
  return {
    candidate_paid_revenue_count: candidates.filter((candidate) => candidate.amount_paid > 0 && candidate.status !== "excluded").length,
    retained_revenue_total: Number(candidates.reduce((sum, candidate) => sum + candidate.retained_amount, 0).toFixed(2)),
    ready_for_review_count: candidates.filter((candidate) => candidate.status === "ready").length,
    blocked_count: candidates.filter((candidate) => candidate.status === "blocked").length,
    excluded_count: candidates.filter((candidate) => candidate.status === "excluded").length,
    already_staged_count: candidates.filter((candidate) => candidate.status === "already_staged").length,
    missing_attribution_count: candidates.filter((candidate) => candidate.missing_attribution).length,
    refund_adjusted_count: candidates.filter((candidate) => candidate.refund_adjusted).length,
  };
}

export function deriveEligibleRevenueReviewData(input: {
  invoices?: EligibleRevenueRow[];
  payments?: EligibleRevenueRow[];
  adjustments?: EligibleRevenueRow[];
  accounts?: EligibleRevenueRow[];
  customers?: EligibleRevenueRow[];
  attributions?: EligibleRevenueRow[];
  assignments?: EligibleRevenueRow[];
  partners?: EligibleRevenueRow[];
  lines?: EligibleRevenueRow[];
  products?: EligibleRevenueRow[];
  prices?: EligibleRevenueRow[];
  stagedEvents?: EligibleRevenueRow[];
}): EligibleRevenueReviewData {
  const accounts = input.accounts || [];
  const customers = input.customers || [];
  const candidates = (input.invoices || []).map((invoice) => {
    const accountId = value(invoice, "account_id");
    const paymentRows = (input.payments || []).filter((row) => value(row, "invoice_id") === value(invoice, "id"));
    const customer = customers.find((row) => value(row, "account_id") === accountId);
    return deriveEligibleRevenueCandidate({
      invoice,
      payments: paymentRows,
      adjustments: (input.adjustments || []).filter((row) => value(row, "invoice_id") === value(invoice, "id")),
      account: accounts.find((row) => value(row, "account_id") === accountId),
      customer,
      attributions: (input.attributions || []).filter((row) => value(row, "account_id") === accountId),
      assignments: input.assignments,
      partners: input.partners,
      lines: (input.lines || []).filter((row) => value(row, "invoice_id") === value(invoice, "id")),
      products: input.products,
      prices: input.prices,
      stagedEvents: (input.stagedEvents || []).filter((row) => value(row, "invoice_id") === value(invoice, "id") || value(row, "payment_id") === value(paymentRows[0], "id")),
    });
  });
  return { candidates, summary: summarizeEligibleRevenueCandidates(candidates) };
}
